/**
 * One row per third-party tracker. Both stacks load from here.
 * Add or remove a tracker in this table; the loaders do not keep their own ids.
 */

export type TrackerCategory = 'analytics' | 'replay' | 'ads' | 'gtm' | 'attribution';

type TrackerRow = {
  category: TrackerCategory;
  /** Public container, pixel, portal, or beacon id. */
  id?: string;
  /** Public env var. Read directly at the call site so Next can inline it. */
  env?: 'NEXT_PUBLIC_MIXPANEL_TOKEN';
  /** Storefront region that must not load this tracker. */
  skipStorefront?: 'US';
};

export const TRACKERS = {
  gtm: { category: 'gtm', id: 'GTM-MRTF5WC' },
  googleAds: { category: 'ads', id: 'AW-614890616' },
  // Loaded on its own only where GTM cannot load (health pages); elsewhere GTM loads it.
  ga4: { category: 'analytics', id: 'G-HV4X6HXMLW' },
  meta: { category: 'ads', id: '528565525249930' },
  tiktok: { category: 'ads', id: 'D25KTORC77UF7183DTR0' },
  reddit: { category: 'ads', id: 'a2_jm19w71venkj' },
  clarity: { category: 'replay', id: 'pyv20l9bkd' },
  mixpanel: {
    category: 'analytics',
    env: 'NEXT_PUBLIC_MIXPANEL_TOKEN',
  },
  hubspot: { category: 'ads', id: '45759574' },
  wewomedia: { category: 'ads', skipStorefront: 'US' },
  tripleWhale: {
    category: 'attribution',
  },
} as const satisfies Record<string, TrackerRow>;

export type TrackerKey = keyof typeof TRACKERS;

export function trackerSkipsStorefront(
  key: TrackerKey,
  region: string | null | undefined,
): boolean {
  const row = TRACKERS[key];
  const skip = 'skipStorefront' in row ? row.skipStorefront : undefined;
  return Boolean(skip && region === skip);
}
